import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { LayoutDashboard, ListChecks, ClipboardList, Wallet as WalletIcon, ArrowDownToLine, Bell, User, LifeBuoy, Users, Building2, Megaphone, FileCheck2, Landmark, BarChart3, ShieldAlert, Settings, ScrollText, PlusCircle, CreditCard, UsersRound, Inbox, UserCheck, MailPlus, Briefcase, HelpCircle } from "lucide-react";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, RequireRole } from "./context/AuthContext";
import { PageLoader } from "./components/shared";
import { PublicLayout, Home, Categories, ForBusinesses } from "./pages/Public";
import { Pricing } from "./pages/Solutions";
import { HowItWorks, FAQ, About, Terms, Privacy, Contact } from "./pages/PublicContent";
import { Seo, ScrollToTop } from "./components/Seo";
import { I18nProvider, CODES } from "./i18n";
const L = (load, name) => lazy(() => load().then((m) => ({ default: name ? m[name] : m.default })));
const Auth = () => import("./pages/Auth");
const UP = () => import("./pages/UserPages");
const Acc = () => import("./pages/Account");
const AP = () => import("./pages/AdminPages");
const AO = () => import("./pages/AdminOps");
const BP = () => import("./pages/BusinessPages");
const PortalLayout = L(() => import("./components/PortalLayout"));
const HelpPage = L(() => import("./pages/HowToUse"));
const Unsubscribe = L(() => import("./pages/Unsubscribe"));
const [Login, Register, ForgotPassword, ResetPassword, VerifyEmail, RegisterGoogle, ConfirmEmail] = ["Login", "Register", "ForgotPassword", "ResetPassword", "VerifyEmail", "RegisterGoogle", "ConfirmEmail"].map((n) => L(Auth, n));
const GoogleCallback = L(() => import("./pages/GoogleAuth"), "GoogleCallback");
const [UserDashboard, Marketplace, TaskDetail, SubmissionDetail, MyTasks] = ["UserDashboard", "Marketplace", "TaskDetail", "SubmissionDetail", "MyTasks"].map((n) => L(UP, n));
const [Wallet, Withdrawals, Notifications, Profile, Support] = ["Wallet", "Withdrawals", "Notifications", "Profile", "Support"].map((n) => L(Acc, n));
const [AdminDashboard, AdminUsers, AdminUserDetail, AdminBusinesses, AdminCampaigns, AdminCampaignDetail, AdminTasks, AdminSubmissions, AdminSubmissionReview] = ["AdminDashboard", "AdminUsers", "AdminUserDetail", "AdminBusinesses", "AdminCampaigns", "AdminCampaignDetail", "AdminTasks", "AdminSubmissions", "AdminSubmissionReview"].map((n) => L(AP, n));
const [AdminWallets, AdminWithdrawals, AdminFinance, AdminFraud, AdminSettings, AdminAudit, AdminSupport] = ["AdminWallets", "AdminWithdrawals", "AdminFinance", "AdminFraud", "AdminSettings", "AdminAudit", "AdminSupport"].map((n) => L(AO, n));
const AdminAssignments = L(() => import("./pages/AdminAssign"), "AdminAssignments");
const AdminEmail = L(() => import("./pages/AdminEmail"), "AdminEmail");
const AdminLeads = L(() => import("./pages/AdminLeads"), "AdminLeads");
const [BusinessOverview, BusinessCampaigns, BusinessCreateCampaign, BusinessCampaignDetail, BusinessResults, BusinessAnalytics, BusinessBilling, BusinessTeam, BusinessSettings] = ["BusinessOverview", "BusinessCampaigns", "BusinessCreateCampaign", "BusinessCampaignDetail", "BusinessResults", "BusinessAnalytics", "BusinessBilling", "BusinessTeam", "BusinessSettings"].map((n) => L(BP, n));

const USER_NAV = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true }, { to: "/app/tasks", label: "Available Tasks", icon: ListChecks },
  { to: "/app/my-tasks", label: "My Tasks", icon: ClipboardList }, { to: "/app/wallet", label: "Earnings", icon: WalletIcon },
  { to: "/app/withdrawals", label: "Withdrawals", icon: ArrowDownToLine }, { to: "/app/notifications", label: "Notifications", icon: Bell },
  { to: "/app/profile", label: "Profile", icon: User }, { to: "/app/support", label: "Support", icon: LifeBuoy }, { to: "/app/help", label: "How to use", icon: HelpCircle },
];
const ADMIN_NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true }, { to: "/admin/users", label: "Users", icon: Users },
  { to: "/admin/businesses", label: "Businesses", icon: Building2 }, { to: "/admin/campaigns", label: "Campaigns", icon: Megaphone },
  { to: "/admin/tasks", label: "Tasks", icon: ListChecks }, { to: "/admin/assignments", label: "Auto-assign", icon: UserCheck }, { to: "/admin/email", label: "Email Center", icon: MailPlus }, { to: "/admin/leads", label: "Leads", icon: Briefcase }, { to: "/admin/submissions", label: "Submissions", icon: FileCheck2 },
  { to: "/admin/wallets", label: "Wallets", icon: WalletIcon }, { to: "/admin/withdrawals", label: "Withdrawals", icon: ArrowDownToLine },
  { to: "/admin/finance", label: "Financial Reports", icon: Landmark }, { to: "/admin/fraud", label: "Fraud Monitoring", icon: ShieldAlert },
  { to: "/admin/notifications", label: "Notifications", icon: Bell }, { to: "/admin/support", label: "Support Inbox", icon: Inbox },
  { to: "/admin/settings", label: "Settings", icon: Settings }, { to: "/admin/audit", label: "Audit Logs", icon: ScrollText }, { to: "/admin/help", label: "How to use", icon: HelpCircle },
];
const BIZ_NAV = [
  { to: "/business", label: "Overview", icon: LayoutDashboard, end: true }, { to: "/business/campaigns", label: "Campaigns", icon: Megaphone, end: true },
  { to: "/business/campaigns/new", label: "Create Campaign", icon: PlusCircle }, { to: "/business/results", label: "Task Results", icon: FileCheck2 },
  { to: "/business/analytics", label: "Analytics", icon: BarChart3 }, { to: "/business/billing", label: "Billing", icon: CreditCard },
  { to: "/business/team", label: "Team", icon: UsersRound }, { to: "/business/settings", label: "Settings", icon: Settings }, { to: "/business/help", label: "How to use", icon: HelpCircle },
];

const P = (prefix, path, el) => <Route key={prefix + path} path={prefix + path === "" ? "/" : prefix + (path === "/" ? "" : path)} element={<><Seo page={path} />{el}</>} />;
const PUBLIC = [["/", <Home />], ["/how-it-works", <HowItWorks />], ["/categories", <Categories />], ["/businesses", <ForBusinesses />], ["/pricing", <Pricing />], ["/about", <About />], ["/terms", <Terms />], ["/privacy", <Privacy />], ["/contact", <Contact />]];
const publicRoutes = (prefix) => [...PUBLIC.map(([path, el]) => P(prefix, path, el)), <Route key={prefix + "/faq"} path={`${prefix}/faq`} element={<FAQ />} />];
const NOINDEX = (title, el) => <><Seo title={title} noindex />{el}</>;

function GoogleReturn({ children }) {
  const { hash } = useLocation();
  if (hash?.includes("session_id=")) return <GoogleCallback hash={hash} />;
  return children;
}

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
      <AuthProvider>
        <BrowserRouter>
          <I18nProvider>
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
          <GoogleReturn>
          <Routes>
            <Route element={<PublicLayout />}>
              {["", ...CODES.map((c) => `/${c}`)].flatMap(publicRoutes)}
            </Route>
            <Route path="/login" element={<><Seo title="Log In" description="Log in to your Rewardlance account to find tasks, track submissions and manage your earnings." /><Login /></>} />
            <Route path="/register" element={<><Seo title="Create Your Free Account" description="Join Rewardlance free. Earn from paid usability tests and surveys, or launch a research campaign for your business." /><Register /></>} />
            <Route path="/register/google" element={NOINDEX("Finish Sign-up", <RegisterGoogle />)} />
            <Route path="/confirm-email" element={NOINDEX("Confirm Your Email", <ConfirmEmail />)} />
            <Route path="/forgot-password" element={NOINDEX("Reset Password", <ForgotPassword />)} />
            <Route path="/reset-password" element={NOINDEX("Choose a New Password", <ResetPassword />)} />
            <Route path="/verify-email" element={NOINDEX("Verify Email", <VerifyEmail />)} />
            <Route path="/unsubscribe" element={NOINDEX("Unsubscribe", <Unsubscribe />)} />
            <Route path="/app" element={<RequireRole roles={["user"]}><PortalLayout nav={USER_NAV} portal="Contributor" base="/app" profilePath="/app/profile" protect /></RequireRole>}>
              <Route index element={<UserDashboard />} />
              <Route path="tasks" element={<Marketplace />} />
              <Route path="tasks/:id" element={<TaskDetail />} />
              <Route path="my-tasks" element={<MyTasks />} />
              <Route path="submissions/:id" element={<SubmissionDetail />} />
              <Route path="wallet" element={<Wallet />} />
              <Route path="transactions" element={<Wallet />} />
              <Route path="withdrawals" element={<Withdrawals />} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="profile" element={<Profile />} />
              <Route path="support" element={<Support />} />
              <Route path="help" element={<HelpPage />} />
            </Route>
            <Route path="/admin" element={<RequireRole roles={["admin"]}><PortalLayout nav={ADMIN_NAV} portal="Super Admin" base="/admin" /></RequireRole>}>
              <Route index element={<AdminDashboard />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="users/:id" element={<AdminUserDetail />} />
              <Route path="businesses" element={<AdminBusinesses />} />
              <Route path="campaigns" element={<AdminCampaigns />} />
              <Route path="campaigns/:id" element={<AdminCampaignDetail />} />
              <Route path="tasks" element={<AdminTasks />} />
              <Route path="submissions" element={<AdminSubmissions />} />
              <Route path="submissions/:id" element={<AdminSubmissionReview />} />
              <Route path="wallets" element={<AdminWallets />} />
              <Route path="withdrawals" element={<AdminWithdrawals />} />
              <Route path="finance" element={<AdminFinance />} />
              <Route path="fraud" element={<AdminFraud />} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="support" element={<AdminSupport />} />
              <Route path="assignments" element={<AdminAssignments />} />
              <Route path="email" element={<AdminEmail />} />
              <Route path="leads" element={<AdminLeads />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="audit" element={<AdminAudit />} />
              <Route path="help" element={<HelpPage />} />
            </Route>
            <Route path="/business" element={<RequireRole roles={["business"]}><PortalLayout nav={BIZ_NAV} portal="Business" base="/business" profilePath="/business/settings" protect /></RequireRole>}>
              <Route index element={<BusinessOverview />} />
              <Route path="campaigns" element={<BusinessCampaigns />} />
              <Route path="campaigns/new" element={<BusinessCreateCampaign />} />
              <Route path="campaigns/:id" element={<BusinessCampaignDetail />} />
              <Route path="results" element={<BusinessResults />} />
              <Route path="analytics" element={<BusinessAnalytics />} />
              <Route path="billing" element={<BusinessBilling />} />
              <Route path="team" element={<BusinessTeam />} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="settings" element={<BusinessSettings />} />
              <Route path="help" element={<HelpPage />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </GoogleReturn>
          </Suspense>
          </I18nProvider>
        </BrowserRouter>
        <Toaster richColors position="top-right" />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
